import { Children, useRef, type ReactNode } from "react";
import { useTimelineCtx } from "../ctx";
import { TimelineStyleEnum } from "../types";
import { TimelinePathSinusoid } from "./timeline-path-sinusoid";
import { TimelineSnakeLine } from "./timeline-snake-line";

interface IProps {
  children: ReactNode;
}

export const TimelineHorizontalNewLine = ({ children }: IProps) => {
  const {
    comboSettings: {
      timelineSetsPerSlide,
      timelineContentPlacement,
      timelineStyle,
      timelineSnakeOrder,
      timelinePosition,
    },
    totalAmtOfItems,
  } = useTimelineCtx();

  const isAlternating = timelineContentPlacement === "alternating";
  const isPath = timelineStyle === TimelineStyleEnum.Path;
  const isAutoWidth = !timelineSetsPerSlide || timelineSetsPerSlide === "auto";
  const snakeGridRef = useRef<HTMLDivElement | null>(null);

  // Alternating + snake need a fixed column count so per-item gridColumn
  // resolves and the snake overlay can compute boustrophedon indices.
  const perRow = isAutoWidth
    ? isAlternating || timelineSnakeOrder
      ? 3
      : null
    : Number(timelineSetsPerSlide);

  if (isAlternating && perRow) {
    // Each row-block uses 3 grid rows (above / track / below). Items
    // beyond perRow wrap to additional row-blocks.
    const totalItems = Children.count(children);
    const rowBlocks = Math.max(1, Math.ceil(totalItems / perRow));
    const rowTemplate = Array(rowBlocks).fill("auto auto auto").join(" ");

    return (
      <div
        className="tw-grid tw-w-full tw-items-stretch"
        style={{
          gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`,
          gridTemplateRows: rowTemplate,
          columnGap: "var(--timeline-items-gap)",
          rowGap: 0,
        }}
      >
        {isPath &&
          Array.from({ length: rowBlocks }).map((_, blockIdx) => {
            const itemsInBlock = Math.min(
              perRow,
              totalItems - blockIdx * perRow,
            );
            const trackRow = blockIdx * 3 + 2;
            // Each row-block's first item is at the top (matches the
            // alternating logic in timeline-item.tsx where alternation
            // restarts at every row boundary).
            const startAtPeak = true;
            return (
              <div
                key={`path-${blockIdx}`}
                className="tw-relative tw-overflow-hidden"
                style={{
                  // Span only the columns occupied so a partial last row
                  // resolves the sinusoid width correctly.
                  gridColumn: `1 / ${itemsInBlock + 1}`,
                  gridRow: trackRow,
                  height: "100%",
                  pointerEvents: "none",
                }}
              >
                <TimelinePathSinusoid
                  itemCount={itemsInBlock}
                  startAtPeak={startAtPeak}
                  firstItemIdx={blockIdx * perRow}
                />
              </div>
            );
          })}
        {children}
      </div>
    );
  }

  // Must come before the generic isAutoWidth branch — snake needs the
  // explicit grid template + fixed perRow so the overlay can compute
  // boustrophedon column indices.
  if (timelineSnakeOrder && perRow) {
    const rowCount = Math.ceil(totalAmtOfItems / perRow);
    return (
      <div className="tw-relative tw-w-full">
        <div
          ref={snakeGridRef}
          className="tw-grid tw-w-full tw-items-stretch"
          style={{
            gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${rowCount}, auto)`,
            gap: "var(--timeline-items-gap)",
          }}
        >
          {children}
        </div>
        <TimelineSnakeLine
          perRow={perRow}
          totalItems={totalAmtOfItems}
          containerRef={snakeGridRef}
          bulletAtBottom={timelinePosition === "bottom"}
        />
      </div>
    );
  }

  if (isAutoWidth) {
    // gridAutoRows: 1fr keeps a uniform row height across the grid so
    // each row doesn't read as its own timeline.
    return (
      <div
        className="tw-grid tw-w-full tw-items-stretch"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gridAutoRows: "1fr",
          gap: "var(--timeline-items-gap)",
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className="tw-grid tw-w-full tw-items-stretch"
      style={{
        gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`,
        gridAutoRows: "1fr",
        gap: "var(--timeline-items-gap)",
      }}
    >
      {children}
    </div>
  );
};
